import { useCallback, useContext, useMemo } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Bath from 'design-system/Iconography/SVG/Bath'
import Bed from 'design-system/Iconography/SVG/Bed'
import Ruler from 'design-system/Iconography/SVG/Ruler'
import Body1 from 'design-system/Typography/Body/Body1'
import Body4 from 'design-system/Typography/Body/Body4'
import Caption from 'design-system/Typography/Caption/Caption'

import Spacer from 'components/Spacer'

import { useWindowSize } from 'services/hooks'
import { ImageTypeEnum, getImageUrl } from 'services/images'

import { formatAddress } from 'utils/listingHelpers'
import { isClosedOrSold } from 'utils/statusHelpers'
import { addCommas } from 'utils/stringFormatting'

import * as T from 'types'
import * as E from 'types/enums'

import OverlayView from './OverlayView'

const TOOLTIP_WIDTH = 55
const TOOLTIP_HEIGHT = 18.5

const SEVEN_FIGURES_TOOLTIP_WIDTH = 60
const EIGHT_FIGURES_TOOLTIP_WIDTH = 68

const CIRCLE_WIDTH = 16

interface ITypeProp {
  type: 'price' | 'circle'
}
interface IStatusProp {
  status: 'sold' | 'active'
}

interface IDisplayPrice {
  displayPrice: number | undefined
}

const getTooltipWidth = (displayPrice: number | undefined) => {
  if (!displayPrice) return TOOLTIP_WIDTH

  if (displayPrice >= 9999999) return EIGHT_FIGURES_TOOLTIP_WIDTH

  if (displayPrice >= 999999) return SEVEN_FIGURES_TOOLTIP_WIDTH

  return TOOLTIP_WIDTH
}

const TooltipContainer = styled.div<ITypeProp>`
  position: absolute;
  top: -${props => (props.type === 'circle' ? CIRCLE_WIDTH : TOOLTIP_HEIGHT)}px;
  left: -${props => (props.type === 'circle' ? CIRCLE_WIDTH : TOOLTIP_WIDTH) / 2 + 5}px;
`

const Circle = styled.div<IStatusProp & IDisplayPrice>`
  position: relative;
  width: ${CIRCLE_WIDTH}px;
  height: ${CIRCLE_WIDTH}px;
  border-radius: 100%;
  background-color: ${props => props.theme.colors[props.status === 'sold' ? 'blue800' : 'gold500']};
  box-shadow: 0px 4px 8px rgba(112, 112, 112, ${props => (props.status === 'active' ? 0.32 : 0.16)});
  border: 3px solid ${props => props.theme.colors.white};
  cursor: pointer;
  text-align: center;

  :hover {
    background-color: ${props =>
    props.theme.colors[props.status === 'sold' ? 'blue700' : 'gold300']};
    z-index: ${props => props.theme.zIndices.level1};
  }

  :hover > .listing-detail {
    display: flex !important;
    z-index: ${props => props.theme.zIndices.level1} !important;
  }

  :hover > div > .pointer {
    background-color: ${props => props.theme.colors.gold300};
  }
`

const PriceTooltip = styled.div<IStatusProp & IDisplayPrice>`
  position: relative;
  padding: 3px 10px;
  width: ${props => getTooltipWidth(props.displayPrice)}px;
  background-color: ${props => props.theme.colors[props.status === 'sold' ? 'blue800' : 'gold500']};
  color: ${props => props.theme.colors.white};
  border-radius: 4px;
  box-shadow: 0 2px 4px 2px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  text-align: center;

  :hover {
    background-color: ${props =>
    props.theme.colors[props.status === 'sold' ? 'blue700' : 'gold300']};
    z-index: ${props => props.theme.zIndices.level1};
  }

  :hover > .listing-detail {
    display: flex !important;
    z-index: ${props => props.theme.zIndices.level1} !important;
  }

  :hover > div > .pointer {
    background-color: ${props =>
    props.theme.colors[props.status === 'sold' ? 'blue700' : 'gold300']};
  }
`

const PopoverPointerContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translate(calc(-50% - 1px), -2px);
`

const PopoverPointer = styled.div<IStatusProp>`
  position: relative;
  background-color: ${props => props.theme.colors[props.status === 'sold' ? 'blue800' : 'gold500']};
  width: 0.5em;
  height: 0.5em;
  border-top-right-radius: 30%;
  transform: rotate(0deg) skewX(-30deg) scale(1, 0.866);

  :before,
  :after {
    content: '';
    position: absolute;
    background-color: inherit;
    width: 0.5em;
    height: 0.5em;
    border-top-right-radius: 30%;
  }

  :before {
    transform: rotate(-135deg) skewX(-45deg) scale(1.414, 0.707) translate(0, -50%);
  }

  :after {
    transform: rotate(135deg) skewY(-45deg) scale(0.707, 1.414) translate(50%);
  }
`

const LISTING_DETAIL_WIDTH = 330

const ListingDetailContainer = styled.div<ITypeProp & IDisplayPrice>`
  z-index: ${props => props.theme.zIndices.level9};
  display: none !important;
  position: absolute;
  bottom: ${props => (props.type === 'price' ? 19 : 10)}px;
  left: 50%;
  transform: translateX(-50%);

  padding-bottom: ${props => props.theme.spacings[8]}px;

  :hover {
    display: flex !important;
  }
`

const ListingDetail = styled.div`
  background-color: ${props => props.theme.colors.white};

  height: 132px;
  width: ${LISTING_DETAIL_WIDTH}px;
  display: flex;
  border-radius: 24px;
  box-shadow: 0 2px 4px 2px rgba(0, 0, 0, 0.2);
  padding: 12px;
  text-align: left;
`

const ListingDetailImage = styled.div`
  flex-shrink: 0;
  position: relative;
  background: ${props => props.theme.colors.neutral50};
  width: 90px;
  height: 100%;
  border-radius: ${props => props.theme.radii[8]}px;
  color: ${props => props.theme.colors.black};
`

const Photo = styled.img`
  position: absolute;
  object-fit: cover;
  width: 100%;
  height: 100%;
  border-radius: ${props => props.theme.radii[8]}px;
`

const ListingInformationContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: ${props => props.theme.colors.black};
`

const ListingDataContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.black};
  padding: 6px 0;
`

const DatumWithIcon = styled.div`
  display: flex;
  align-items: center;
  column-gap: 4px;

  p {
    padding-top: 1px; /* font correction */
  }
`

const IconContainer = styled.div`
  height: 24px;
  width: 24px;
  border-radius: 100%;
  background-color: ${props => props.theme.colors.blue50};
  display: flex;
  align-items: center;
  justify-content: center;
`

interface CustomMarkerProps {
  listing: T.IListing
  map?: google.maps.Map
  type: 'price' | 'circle'
  onClick: (payload: T.IListing) => void
  onHover: (payload: T.IListing) => void
  hoverActive?: boolean
}

const CustomMarker = ({
  listing,
  map,
  type = 'circle',
  onClick,
  onHover,
  hoverActive,
}: CustomMarkerProps) => {
  const theme = useContext(ThemeContext)
  const { width } = useWindowSize()
  const isDesktop = width && width >= theme.metrics.desktop

  const handleHover = () => {
    if (hoverActive) return

    onHover(listing)
  }

  const handleClick = useCallback(() => {
    if (!isDesktop) return handleHover()

    onClick(listing)
  }, [onClick, listing])

  const handleClickListingDetail = useCallback(() => {
    onClick(listing)
  }, [onClick, listing])

  const formattedPrice = useMemo(() => {
    if (!listing.displayPrice) return '-'

    if (listing.displayPrice < 1000000) return `$${(listing.displayPrice / 1000).toFixed(0)}k`

    return `$${(listing.displayPrice / 1000000).toFixed(2).replace(/\.?0+$/, '')}m`
  }, [listing.displayPrice])

  const photoSrc = useMemo(
    () => getImageUrl(listing._id, listing.photos?.[0], ImageTypeEnum.ListingCard),
    [listing.photos],
  )

  const baths = useMemo(() => {
    let bathWithHalf = listing.bath ? listing.bath : 0

    if (listing.bathHalf) {
      bathWithHalf += listing.bathHalf ? 0.5 : 0
    }

    return bathWithHalf
  }, [listing.bath, listing.bathHalf])

  if (!map) return null

  // one of
  const status = isClosedOrSold(listing) ? 'sold' : 'active'
  const zIndex = theme.zIndices[status === 'active' ? 'level2' : 'level1']

  const Tooltip = type === 'price' ? PriceTooltip : Circle

  return (
    <OverlayView
      lat={listing.location.coordinates[1]}
      lng={listing.location.coordinates[0]}
      map={map}
      zIndex={hoverActive ? theme.zIndices.level9 : zIndex}
    >
      <TooltipContainer
        onMouseEnter={handleHover}
        onClick={handleClick}
        type={type}
      >
        <Tooltip
          status={status}
          displayPrice={listing.displayPrice}
        >
          {type === 'price' && (
            <Caption
              color={status === 'active' ? 'neutral900' : 'white'}
              textAlign="center"
            >
              {formattedPrice}
            </Caption>
          )}
          {/* hidden until hover on desktop, click on mobile */}
          <ListingDetailContainer
            className="listing-detail"
            onClick={handleClickListingDetail}
            type={type}
            displayPrice={listing.displayPrice}
          >
            <ListingDetail>
              <ListingDetailImage>
                <Photo src={photoSrc} />
              </ListingDetailImage>
              <Spacer size={16} />
              <ListingInformationContainer>
                {listing.displayPrice && <Body1>{`$${addCommas(listing.displayPrice)}`}</Body1>}
                <ListingDataContainer>
                  {listing.propertyType !== E.HomeTypeEnum.land
                    ? (
                      <>
                        <DatumWithIcon>
                          <IconContainer>
                            <Bed
                              fill={theme.colors.blue500}
                              width={15}
                              height={15}
                            />
                          </IconContainer>
                          <Body4>{listing.bed}</Body4>
                        </DatumWithIcon>
                        <Spacer size={12} />
                        <DatumWithIcon>
                          <IconContainer>
                            <Bath
                              fill={theme.colors.blue500}
                              width={15}
                              height={15}
                            />
                          </IconContainer>
                          <Body4>{baths} </Body4>
                        </DatumWithIcon>
                        <Spacer size={12} />
                        <DatumWithIcon>
                          <IconContainer>
                            <Ruler
                              fill={theme.colors.blue500}
                              width={15}
                              height={15}
                            />
                          </IconContainer>
                          <Body4>{addCommas(listing.sqft || '')}</Body4>
                        </DatumWithIcon>
                      </>
                    )
                    : (
                      <Body4 tag="span">{`${listing.lotSize || 0} ${
                        listing.lotSizeType || 'Acres'
                      }`}</Body4>
                    )}
                </ListingDataContainer>
                <Body4>{formatAddress(listing)}</Body4>
              </ListingInformationContainer>
            </ListingDetail>
          </ListingDetailContainer>
          {type === 'price' && (
            <PopoverPointerContainer>
              <PopoverPointer
                className="pointer"
                status={status}
              />
            </PopoverPointerContainer>
          )}
        </Tooltip>
      </TooltipContainer>
    </OverlayView>
  )
}

export default CustomMarker
